import React, { useState, useMemo } from 'react';
import {
  Search,
  TrendingUp,
  Sparkles,
  Layers,
  Activity,
  Plus,
  CheckCircle2,
  AlertCircle,
  HelpCircle,
  BarChart2,
  DollarSign,
  Users
} from 'lucide-react';
import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
  CartesianGrid
} from 'recharts';
import type {
  KeywordResearchResult,
  KeywordSuggestion
} from '../campaignsApi';
import './keyword-research-panel.css';

interface KeywordResearchPanelProps {
  data: KeywordResearchResult;
  isLight?: boolean;
  mode?: 'interactive' | 'readonly';
  selectedKeywords?: string[];
  selectedPains?: string[];
  highlightBudget?: number;
  onToggleKeyword?: (keyword: string) => void;
  onTogglePain?: (pain: string) => void;
  onAddCustomKeyword?: (keyword: string) => void;
  onAddCustomPain?: (pain: string) => void;
  onReResearch?: () => void;
  isResearching?: boolean;
}

export const KeywordResearchPanel: React.FC<KeywordResearchPanelProps> = ({
  data,
  isLight = false,
  mode = 'interactive',
  selectedKeywords = [],
  selectedPains = [],
  highlightBudget,
  onToggleKeyword,
  onTogglePain,
  onAddCustomKeyword,
  onAddCustomPain,
  onReResearch,
  isResearching = false,
}) => {
  const [customKeywordInput, setCustomKeywordInput] = useState('');
  const [customPainInput, setCustomPainInput] = useState('');

  const isInteractive = mode === 'interactive';

  // Dores combinadas (Catálogo + IA)
  const allPains = useMemo(() => {
    const combined = new Set<string>();
    (data.catalogPains || []).forEach(p => combined.add(p));
    (data.expandedPains || []).forEach(p => combined.add(p));
    // Inclui selecionadas que possam ser custom
    selectedPains.forEach(p => combined.add(p));
    return Array.from(combined);
  }, [data.catalogPains, data.expandedPains, selectedPains]);

  // Agrupamento de Keywords por Intenção de Funil
  const funnelKeywords = useMemo(() => {
    const awareness: KeywordSuggestion[] = [];
    const consideration: KeywordSuggestion[] = [];
    const decision: KeywordSuggestion[] = [];

    (data.topKeywords || []).forEach((k) => {
      if (k.intent === 'decision' || k.consciousnessLevel === 'most_aware') {
        decision.push(k);
      } else if (k.intent === 'consideration' || k.consciousnessLevel === 'solution_aware' || k.consciousnessLevel === 'product_aware') {
        consideration.push(k);
      } else {
        awareness.push(k);
      }
    });

    return { awareness, consideration, decision };
  }, [data.topKeywords]);

  // Formatação dos dados para o Recharts (combinando as 3 séries no mesmo eixo X de dias)
  const chartData = useMemo(() => {
    const seriesList = data?.reachProjection?.series || [];
    if (!seriesList.length) return [];

    const daysCount = seriesList[0]?.data?.length || 30;
    const points = [];

    for (let i = 0; i < daysCount; i++) {
      const dayNum = i + 1;
      const point: any = { day: dayNum, name: `Dia ${dayNum}` };

      seriesList.forEach((s) => {
        const d = s.data[i];
        if (d) {
          // chaves com nome da série para cada linha do Recharts
          point[`reach_${s.budget}`] = d.reachAvg;
          point[`spent_${s.budget}`] = d.spent;
        }
      });
      points.push(point);
    }
    return points;
  }, [data?.reachProjection?.series]);

  const handleAddKeyword = (e: React.FormEvent) => {
    e.preventDefault();
    if (customKeywordInput.trim() && onAddCustomKeyword) {
      onAddCustomKeyword(customKeywordInput.trim());
      setCustomKeywordInput('');
    }
  };

  const handleAddPain = (e: React.FormEvent) => {
    e.preventDefault();
    if (customPainInput.trim() && onAddCustomPain) {
      onAddCustomPain(customPainInput.trim());
      setCustomPainInput('');
    }
  };

  const colors = ['#3b82f6', '#10b981', '#8b5cf6'];

  return (
    <div className="krp-container animate-in">
      {/* 1. Dores do Mercado Validadas */}
      <div className={`krp-card ${isLight ? 'light' : 'dark'}`}>
        <div className="krp-header-title">
          <h4 className="krp-section-title" style={{ color: isLight ? '#0f172a' : '#fff' }}>
            <Layers size={18} style={{ color: '#ec4899' }} /> Dores Mapeadas no Mercado & IA
          </h4>
          <span className="krp-badge" style={{ background: isLight ? '#fce7f3' : 'rgba(236,72,153,0.15)', color: isLight ? '#be185d' : '#f472b6' }}>
            {selectedPains.length} selecionadas de {allPains.length}
          </span>
        </div>

        <p style={{ fontSize: '0.8rem', color: isLight ? '#64748b' : 'var(--text-muted)', marginBottom: '0.85rem' }}>
          {isInteractive
            ? 'Marque ou desmarque as dores que o CGO usará como ganchos de persuasão nos criativos.'
            : 'Dores ativas utilizadas na estruturação dos criativos e quebra de objeções.'}
        </p>

        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
          {allPains.map((pain) => {
            const isSelected = selectedPains.includes(pain);
            const isFromCatalog = (data.catalogPains || []).includes(pain);

            return (
              <div
                key={pain}
                onClick={() => isInteractive && onTogglePain && onTogglePain(pain)}
                className={`krp-tag-item ${isLight ? 'light' : 'dark'} ${isSelected ? 'selected' : ''}`}
                style={{ cursor: isInteractive ? 'pointer' : 'default' }}
              >
                {isInteractive && (
                  <input
                    type="checkbox"
                    checked={isSelected}
                    readOnly
                    style={{ accentColor: '#ec4899', pointerEvents: 'none' }}
                  />
                )}
                <span>{pain}</span>
                <span style={{ fontSize: '0.68rem', opacity: 0.7, padding: '0.1rem 0.35rem', borderRadius: '4px', background: isLight ? '#e2e8f0' : 'rgba(255,255,255,0.08)' }}>
                  {isFromCatalog ? '📋 Catálogo' : '🤖 IA'}
                </span>
              </div>
            );
          })}
        </div>

        {isInteractive && onAddCustomPain && (
          <form onSubmit={handleAddPain} className="krp-custom-input-row">
            <input
              type="text"
              placeholder="Adicionar outra dor específica do seu público..."
              value={customPainInput}
              onChange={(e) => setCustomPainInput(e.target.value)}
              className={`krp-custom-input ${isLight ? 'light' : 'dark'}`}
            />
            <button
              type="submit"
              disabled={!customPainInput.trim()}
              className="secondary-button"
              style={{ display: 'flex', alignItems: 'center', gap: '0.3rem', fontSize: '0.78rem', padding: '0.45rem 0.85rem' }}
            >
              <Plus size={14} /> Adicionar
            </button>
          </form>
        )}
      </div>

      {/* 2. Palavras-chave Validadas por Funil & Intenção */}
      <div className={`krp-card ${isLight ? 'light' : 'dark'}`}>
        <div className="krp-header-title">
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <h4 className="krp-section-title" style={{ color: isLight ? '#0f172a' : '#fff' }}>
              <Search size={18} style={{ color: '#3b82f6' }} /> Palavras-Chave de Busca Google & Relevância
            </h4>
            {data?.risingTerms?.length > 0 && (
              <span className="krp-badge" style={{ background: isLight ? '#fef3c7' : 'rgba(245,158,11,0.15)', color: isLight ? '#b45309' : '#fbbf24' }}>
                <TrendingUp size={12} /> {data.risingTerms.length} em alta
              </span>
            )}
          </div>

          <span className="krp-badge" style={{ background: isLight ? '#eff6ff' : 'rgba(59,130,246,0.15)', color: isLight ? '#1d4ed8' : '#93c5fd' }}>
            {selectedKeywords.length} selecionadas de {data?.topKeywords?.length || 0}
          </span>
        </div>

        {data?.trendsSummary && (
          <div style={{ background: isLight ? '#f8fafc' : 'rgba(255,255,255,0.02)', padding: '0.75rem 1rem', borderRadius: '8px', border: isLight ? '1px solid #e2e8f0' : '1px solid rgba(255,255,255,0.05)', marginBottom: '1rem', fontSize: '0.8rem', color: isLight ? '#475569' : 'var(--text-muted)' }}>
            💡 <strong style={{ color: isLight ? '#0f172a' : '#fff' }}>Diagnóstico de Busca:</strong> {data.trendsSummary}
          </div>
        )}

        <div className="krp-funnel-grid">
          {/* Topo de Funil */}
          <div className={`krp-funnel-col ${isLight ? 'light' : 'dark'}`}>
            <div className="krp-funnel-col-title" style={{ color: '#3b82f6' }}>
              <span>1. Topo (Awareness)</span>
              <span style={{ fontSize: '0.7rem', opacity: 0.8 }}>Sintomas & Sinais</span>
            </div>
            {funnelKeywords.awareness.map((k) => (
              <KeywordItem
                key={k.keyword}
                item={k}
                isSelected={selectedKeywords.includes(k.keyword)}
                isInteractive={isInteractive}
                isLight={isLight}
                onToggle={() => isInteractive && onToggleKeyword && onToggleKeyword(k.keyword)}
              />
            ))}
            {funnelKeywords.awareness.length === 0 && (
              <span style={{ fontSize: '0.75rem', color: isLight ? '#94a3b8' : 'var(--text-muted)', fontStyle: 'italic' }}>Nenhuma detectada</span>
            )}
          </div>

          {/* Meio de Funil */}
          <div className={`krp-funnel-col ${isLight ? 'light' : 'dark'}`}>
            <div className="krp-funnel-col-title" style={{ color: '#10b981' }}>
              <span>2. Meio (Consideration)</span>
              <span style={{ fontSize: '0.7rem', opacity: 0.8 }}>Soluções & Métodos</span>
            </div>
            {funnelKeywords.consideration.map((k) => (
              <KeywordItem
                key={k.keyword}
                item={k}
                isSelected={selectedKeywords.includes(k.keyword)}
                isInteractive={isInteractive}
                isLight={isLight}
                onToggle={() => isInteractive && onToggleKeyword && onToggleKeyword(k.keyword)}
              />
            ))}
            {funnelKeywords.consideration.length === 0 && (
              <span style={{ fontSize: '0.75rem', color: isLight ? '#94a3b8' : 'var(--text-muted)', fontStyle: 'italic' }}>Nenhuma detectada</span>
            )}
          </div>

          {/* Fundo de Funil */}
          <div className={`krp-funnel-col ${isLight ? 'light' : 'dark'}`}>
            <div className="krp-funnel-col-title" style={{ color: '#8b5cf6' }}>
              <span>3. Fundo (Decision)</span>
              <span style={{ fontSize: '0.7rem', opacity: 0.8 }}>Compra & Ferramenta</span>
            </div>
            {funnelKeywords.decision.map((k) => (
              <KeywordItem
                key={k.keyword}
                item={k}
                isSelected={selectedKeywords.includes(k.keyword)}
                isInteractive={isInteractive}
                isLight={isLight}
                onToggle={() => isInteractive && onToggleKeyword && onToggleKeyword(k.keyword)}
              />
            ))}
            {funnelKeywords.decision.length === 0 && (
              <span style={{ fontSize: '0.75rem', color: isLight ? '#94a3b8' : 'var(--text-muted)', fontStyle: 'italic' }}>Nenhuma detectada</span>
            )}
          </div>
        </div>

        {isInteractive && onAddCustomKeyword && (
          <form onSubmit={handleAddKeyword} className="krp-custom-input-row" style={{ marginTop: '1rem' }}>
            <input
              type="text"
              placeholder="Adicionar outra palavra-chave de busca..."
              value={customKeywordInput}
              onChange={(e) => setCustomKeywordInput(e.target.value)}
              className={`krp-custom-input ${isLight ? 'light' : 'dark'}`}
            />
            <button
              type="submit"
              disabled={!customKeywordInput.trim()}
              className="secondary-button"
              style={{ display: 'flex', alignItems: 'center', gap: '0.3rem', fontSize: '0.78rem', padding: '0.45rem 0.85rem' }}
            >
              <Plus size={14} /> Adicionar
            </button>
          </form>
        )}
      </div>

      {/* 3. Projeção Dinâmica de Alcance por Investimento (Piso 1x, Dobro 2x, Quádruplo 4x) */}
      <div className={`krp-card ${isLight ? 'light' : 'dark'}`}>
        <div className="krp-header-title">
          <div>
            <h4 className="krp-section-title" style={{ color: isLight ? '#0f172a' : '#fff' }}>
              <BarChart2 size={18} style={{ color: '#10b981' }} /> Projeção de Alcance por Investimento Diário (30 Dias)
            </h4>
            <span style={{ fontSize: '0.78rem', color: isLight ? '#64748b' : 'var(--text-muted)' }}>
              Estimativa acumulada baseada no CPM médio de R$ {data?.reachProjection?.cpmEstimate?.avg || 22}/mil para este nicho.
            </span>
          </div>

          <span className="krp-badge" style={{ background: isLight ? '#dcfce7' : 'rgba(16,185,129,0.15)', color: isLight ? '#15803d' : '#34d399' }}>
            Base Piso: R$ {data?.reachProjection?.baseBudget || 50}/dia
          </span>
        </div>

        {/* Gráfico Recharts de Linhas */}
        <div className="krp-chart-wrapper">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={chartData} margin={{ top: 10, right: 20, left: -10, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke={isLight ? '#e2e8f0' : 'rgba(255,255,255,0.06)'} />
              <XAxis
                dataKey="day"
                tick={{ fill: isLight ? '#64748b' : '#94a3b8', fontSize: 11 }}
                tickLine={false}
                axisLine={{ stroke: isLight ? '#cbd5e1' : 'rgba(255,255,255,0.1)' }}
                tickFormatter={(val) => `D${val}`}
              />
              <YAxis
                tick={{ fill: isLight ? '#64748b' : '#94a3b8', fontSize: 11 }}
                tickLine={false}
                axisLine={{ stroke: isLight ? '#cbd5e1' : 'rgba(255,255,255,0.1)' }}
                tickFormatter={(val) => (val >= 1000 ? `${(val / 1000).toFixed(0)}k` : val)}
              />
              <Tooltip content={<CustomTooltip isLight={isLight} series={data?.reachProjection?.series || []} />} />
              <Legend wrapperStyle={{ fontSize: '0.78rem', paddingTop: '8px' }} />

              {(data?.reachProjection?.series || []).map((s, idx) => {
                const strokeColor = colors[idx % colors.length];
                return (
                  <Line
                    key={s.budget}
                    type="monotone"
                    dataKey={`reach_${s.budget}`}
                    name={s.label}
                    stroke={strokeColor}
                    strokeWidth={s.budget === data?.reachProjection?.baseBudget ? 3 : 2}
                    dot={false}
                    activeDot={{ r: 5 }}
                    animationDuration={1000}
                  />
                );
              })}
            </LineChart>
          </ResponsiveContainer>
        </div>

        {/* Cards de Métricas Consolidadas para os 3 Cenários (Piso, Dobro, Quádruplo) */}
        <div className="krp-metrics-grid">
          {(data?.reachProjection?.metrics || []).map((m, idx) => {
            const isBase = m.budget === data?.reachProjection?.baseBudget;
            const accent = colors[idx % colors.length];

            return (
              <div
                key={m.budget}
                className={`krp-metric-card ${isLight ? 'light' : 'dark'} ${isBase ? 'highlight' : ''}`}
                style={{ borderLeft: `4px solid ${accent}` }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <strong style={{ fontSize: '0.85rem', color: isLight ? '#0f172a' : '#fff' }}>
                    R$ {m.budget}/dia
                  </strong>
                  <span style={{ fontSize: '0.7rem', fontWeight: 600, color: accent, background: `${accent}15`, padding: '0.15rem 0.4rem', borderRadius: '4px' }}>
                    {m.multiplier}
                  </span>
                </div>

                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.15rem', marginTop: '0.35rem' }}>
                  <span style={{ fontSize: '0.75rem', color: isLight ? '#64748b' : 'var(--text-muted)' }}>
                    Gasto 30d: <strong style={{ color: isLight ? '#0f172a' : '#e2e8f0' }}>R$ {m.monthlySpend.toLocaleString('pt-BR')}</strong>
                  </span>
                  <span style={{ fontSize: '0.75rem', color: isLight ? '#64748b' : 'var(--text-muted)' }}>
                    Alcance: <strong style={{ color: isLight ? '#0f172a' : '#e2e8f0' }}>~{m.estimatedReach.toLocaleString('pt-BR')} pessoas</strong>
                  </span>
                  <span style={{ fontSize: '0.75rem', color: isLight ? '#059669' : '#34d399' }}>
                    Leads Previstos: <strong>~{m.estimatedLeads}</strong> (CPL ~R$ {m.estimatedCPL})
                  </span>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

// Componente de Item Individual de Palavra-chave
const KeywordItem: React.FC<{
  item: KeywordSuggestion;
  isSelected: boolean;
  isInteractive: boolean;
  isLight: boolean;
  onToggle: () => void;
}> = ({ item, isSelected, isInteractive, isLight, onToggle }) => {
  return (
    <div
      onClick={onToggle}
      className={`krp-tag-item ${isLight ? 'light' : 'dark'} ${isSelected ? 'selected' : ''}`}
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        cursor: isInteractive ? 'pointer' : 'default',
        padding: '0.35rem 0.55rem',
        fontSize: '0.78rem'
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', overflow: 'hidden' }}>
        {isInteractive && (
          <input
            type="checkbox"
            checked={isSelected}
            readOnly
            style={{ accentColor: '#3b82f6', pointerEvents: 'none' }}
          />
        )}
        <span style={{ textOverflow: 'ellipsis', whiteSpace: 'nowrap', overflow: 'hidden' }}>
          {item.keyword}
        </span>
      </div>

      <div style={{ display: 'flex', alignItems: 'center', gap: '0.3rem', flexShrink: 0 }}>
        {item.trend === 'rising' && (
          <span style={{ color: '#10b981', fontSize: '0.7rem', fontWeight: 700 }} title="Tendência de alta">↑</span>
        )}
        <span style={{ fontSize: '0.65rem', opacity: 0.6, background: isLight ? '#e2e8f0' : 'rgba(255,255,255,0.06)', padding: '0.05rem 0.3rem', borderRadius: '4px' }}>
          {item.score}%
        </span>
      </div>
    </div>
  );
};

// Tooltip customizado para o Recharts
const CustomTooltip = ({ active, payload, label, isLight, series }: any) => {
  if (active && payload && payload.length) {
    return (
      <div style={{
        background: isLight ? '#ffffff' : '#1e1e24',
        border: isLight ? '1px solid #cbd5e1' : '1px solid rgba(255,255,255,0.1)',
        borderRadius: '8px',
        padding: '0.6rem 0.8rem',
        boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
        fontSize: '0.75rem'
      }}>
        <strong style={{ display: 'block', marginBottom: '0.3rem', color: isLight ? '#0f172a' : '#fff' }}>
          {label} (Acumulado)
        </strong>
        {payload.map((entry: any, index: number) => {
          const s = series.find((item: any) => `reach_${item.budget}` === entry.dataKey);
          const spent = entry.payload?.[`spent_${s?.budget}`] || 0;

          return (
            <div key={`item-${index}`} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.2rem' }}>
              <span style={{ width: '8px', height: '8px', borderRadius: '50%', background: entry.color }} />
              <span style={{ color: isLight ? '#334155' : '#cbd5e1' }}>
                {entry.name}: <strong>~{entry.value?.toLocaleString('pt-BR')} pessoas</strong> (Gasto: R$ {spent})
              </span>
            </div>
          );
        })}
      </div>
    );
  }
  return null;
};
